import { apiPost } from "@/shared/api";
import type { Order } from "./types";

/** Fornitori sul cui portale OM sa ordinare da solo. Ityhome e Itamoby via
 *  WooCommerce (stesso motore, cambia il dominio); Bizzotto con un browser
 *  headless che gira in un container a parte — il suo portale è PrimeFaces e i
 *  prezzi li disegna JavaScript. */
export type PortalSupplierId = "ityhome" | "itamoby" | "bizzotto";

/** Ordini ordinabili su Ityhome: gate del bottone "Ordina a Ityhome".
 *  Il tasto agisce SOLO sul sottoinsieme del fornitore, come Cosma e WF
 *  (regola anti-errore Davide 2026-06-08).
 *
 *  Guarda ``portaleFornitore`` e non ``fornitore.kind``: sugli ordini Poleepo
 *  senza ``supplier_name`` il kind resta ``unknown`` e il tasto spariva su
 *  ordini che il fornitore vende eccome (caso BB920395181-F1). */
export function ordersItyhome<T extends Pick<Order, "portaleFornitore">>(orders: T[]): T[] {
  return orders.filter((o) => o.portaleFornitore === "ityhome");
}

/** Ordini Itamoby nella selezione: gate del bottone "Ordina a Itamoby". */
export function ordersItamoby<T extends Pick<Order, "portaleFornitore">>(orders: T[]): T[] {
  return orders.filter((o) => o.portaleFornitore === "itamoby");
}

/** Ordini Bizzotto nella selezione: gate del bottone "Ordina a Bizzotto".
 *
 *  ⚠️ Bizzotto consegna a BS COM, non al cliente: questo tasto compra a
 *  magazzino, e la spedizione al cliente resta nostra. */
export function ordersBizzotto<T extends Pick<Order, "portaleFornitore">>(orders: T[]): T[] {
  return orders.filter((o) => o.portaleFornitore === "bizzotto");
}

/** I fornitori su cui OM sa ordinare da solo: gate del tasto in card. */
export const FORNITORI_CON_PORTALE: readonly string[] = ["ityhome", "itamoby", "bizzotto"];

export type PortalError = {
  order_id: string;
  message: string;
};

export type PortalItem = {
  sku: string;
  title: string;
  product_id: number;
  quantity: number;
  /** Prezzo B2B letto dal portale da loggati, IVA esclusa. */
  unit_price: number;
  line_total: number;
  in_stock: boolean;
  /** Riga aggiunta da OM (il pallet), non presente nell'ordine cliente. */
  is_bancale?: boolean;
};

export type PortalPreviewOrder = {
  order_id: number;
  reference: string;
  items: PortalItem[];
  total_net: number;
  total_gross: number;
  /** Ha già un numero fornitore o è nel sent-log: non verrà ri-ordinato. */
  already_ordered: boolean;
  warnings: string[];
};

export type PortalPreviewResult = {
  supplier_id: string;
  orders: PortalPreviewOrder[];
  errors: PortalError[];
  /** Somma ivata dei soli ordini che partirebbero: l'importo del bonifico. */
  total_gross: number;
};

export type PortalOrderedItem = {
  order_id: number;
  reference: string;
  /** Es. "ITYH-80416" — stessa forma che scrive la mail di conferma. */
  supplier_order_ref: string;
  numero: string;
  url: string;
  total_net: number;
  total_gross: number;
  /** Bizzotto: peso e ingombro dichiarati dal riepilogo del portale. La merce
   *  arriva a BS COM, e chi la riceve vuole saperlo. */
  peso_lordo_kg?: number | null;
  cubatura_mc?: number | null;
  /** `false` = dopo la conferma il portale non si è mosso: l'ordine POTREBBE
   *  non essere partito e va guardato sul portale. */
  conferma_vista?: boolean;
};

export type PortalSkippedItem = {
  order_id: number | null;
  reference: string;
  reason: string;
};

export type PortalOrderBulkResult = {
  requested: number;
  ordered: PortalOrderedItem[];
  skipped: PortalSkippedItem[];
  errors: PortalError[];
  total_gross: number;
};

/** Cosa verrebbe ordinato sul portale: righe, prezzi B2B, totale da bonificare.
 *  Fa login sul portale in sola lettura — nessun carrello toccato, nessun
 *  ordine creato. È ciò che riempie il popup di conferma. */
export function fetchPortalPreview(
  supplierId: PortalSupplierId,
  orderIds: string[],
): Promise<PortalPreviewResult> {
  return apiPost<PortalPreviewResult>(`/api/suppliers/${supplierId}/orders/portal-preview`, {
    order_ids: orderIds,
  });
}

/** Crea gli ordini sul portale del fornitore, uno alla volta. IRREVERSIBILE:
 *  chiamare solo dopo la conferma esplicita nel popup. ``force`` riordina
 *  anche ciò che risulta già ordinato (bypassa la guardia anti-doppione). */
export function placePortalOrders(
  supplierId: PortalSupplierId,
  orderRefs: string[],
  force = false,
): Promise<PortalOrderBulkResult> {
  return apiPost<PortalOrderBulkResult>(`/api/suppliers/${supplierId}/orders/portal-order-bulk`, {
    order_ids: orderRefs,
    force,
  });
}
